import { colors } from '../../utils/theme';

import type { ImageObject, SliderMark } from './types';

const NUMBER_OF_INCIPIENT_MARKS = 6;
const MARK_TO_BOX_PERCENTAGE_OF_IMAGE = new Map<number, number>([
  [0, 0],
  [(1 / NUMBER_OF_INCIPIENT_MARKS) * 100, 0.09],
  [(2 / NUMBER_OF_INCIPIENT_MARKS) * 100, 0.1054],
  [(3 / NUMBER_OF_INCIPIENT_MARKS) * 100, 0.2357],
  [(4 / NUMBER_OF_INCIPIENT_MARKS) * 100, 0.6059],
  [(5 / NUMBER_OF_INCIPIENT_MARKS) * 100, 1.61019],
  [100, 100],
]);

const INSIPIENT_MARKS: SliderMark[] = [
  { value: 0 },
  { value: (1 / NUMBER_OF_INCIPIENT_MARKS) * 100 },
  { value: (2 / NUMBER_OF_INCIPIENT_MARKS) * 100 },
  { value: (3 / NUMBER_OF_INCIPIENT_MARKS) * 100 },
  { value: (4 / NUMBER_OF_INCIPIENT_MARKS) * 100 },
  { value: (5 / NUMBER_OF_INCIPIENT_MARKS) * 100 },
  { value: 100 },
];

const TEST_IMAGE: ImageObject = {
  analysisState: 'Success',
  objectId: 'abc123',
  imageId: 'abc123',
  models: {
    caries: {
      findings: [],
      modelId: '',
      modelState: '',
      vcaMessage: '',
    },
    periapical_radiolucency: {
      findings: [],
      modelId: '',
      modelState: '',
    },
    tooth_identification: {
      findings: [],
      modelId: '',
      modelState: '',
    },
    radiographic_boneloss: {
      findings: [],
      modelId: '',
      modelState: '',
    },
  },
  videaErrorCode: 'N/A',
  imageKeys: {
    s3path: '',
    original: {
      key: '',
      version: '',
    },
    sharpened: {
      key: '',
      version: '',
    },
    sharpest: {
      key: '',
      version: '',
    },
    enhanced: {
      key: '',
      version: '',
    },
    generalized: {
      key: '',
      version: '',
    },
  },
  imageMetadata: {
    anatomicRegionSequenceName: '',
  },
  fileDateCreated: '2022',
  path: 's3://',
  fileName: 'R1.rvg',
  patientInfo: {
    patientId: '',
    analysisDate: '',
    dataSource: '',
    displayed: false,
    estimatedVisitDate: '',
    examDate: '',
    id: '',
    latestVisitAnalyzedImagesCount: 2,
    name: '',
  },
  userAnnotations: [],
  rotation: 0,
  imageIndex: 1,
  imageDimension: {
    widthPx: 800,
    heightPx: 600,
  },
};

export default {
  /**
   * Not using it now but would like to keep it for now since we may find it useful when we decide to leverage tooth id more
   * Refer to the diagram in https://videahealth.atlassian.net/wiki/spaces/SOFTWARE/pages/2293071876/FMX+View+Image+Ordering
   * for a visual look at the data structure below
   */
  TOOTH_QUADRANT_RANGES: {
    SIMPLE: ['1-8', '9-16', '17-24', '25-32'],
    BITEWING: [
      { index: 7, top: [1, 3], bottom: [32, 30] },
      { index: 8, top: [2, 6], bottom: [31, 27] },
      { index: 9, top: [13, 15], bottom: [18, 20] },
      { index: 10, top: [14, 16], bottom: [17, 19] },
    ],
    OTHER: [
      { index: 0, teeth: [1, 4] },
      { index: 1, teeth: [2, 6] },
      { index: 2, teeth: [3, 6] },
      { index: 3, teeth: [6, 13] },
      { index: 4, teeth: [9, 14] },
      { index: 5, teeth: [13, 15] },
      { index: 6, teeth: [14, 16] },
      { index: 11, teeth: [32, 30] },
      { index: 12, teeth: [31, 27] },
      { index: 13, teeth: [30, 27] },
      { index: 14, teeth: [23, 27] },
      { index: 15, teeth: [22, 27] },
      { index: 16, teeth: [19, 22] },
      { index: 17, teeth: [17, 19] },
    ],
  },
  BW_ROW_INDEXES: [7, 8, 9, 10],
  ADD_USER_ANNOTATION_SUCCESS: 'ADD_USER_ANNOTATION/SUCCESS',
  ADD_USER_ANNOTATION_FAILURE: 'ADD_USER_ANNOTATION/FAILURE',
  CLEAR_PATIENT_IMAGES: 'CLEAR_PATIENT_IMAGES',
  DELETE_USER_ANNOTATION_SUCCESS: 'DELETE_USER_ANNOTATION/SUCCESS',
  DELETE_USER_ANNOTATION_FAILURE: 'DELETE_USER_ANNOTATION/FAILURE',
  FETCH_AI_RESULTS_POLLING: 'FETCH_AI_RESULTS/POLLING',
  FETCH_AI_RESULTS_SUCCESS: 'FETCH_AI_RESULTS/SUCCESS',
  FETCH_PATIENT_ANALYSES_SUCCESS: 'FETCH_PATIENT_ANALYSES/SUCCESS',
  FETCH_PATIENT_ANALYSES_FAILURE: 'FETCH_PATIENT_ANALYSES/FAILURE',
  FETCH_CONFIDENCE_THRESHOLDS_SUCCESS: 'FETCH_CONFIDENCE_THRESHOLDS/SUCCESS',
  FETCH_CONFIDENCE_THRESHOLDS_FAILURE: 'FETCH_CONFIDENCE_THRESHOLDS/FAILURE',
  FETCH_LATEST_PATIENTS_SUCCESS: 'FETCH_LATEST_PATIENTS/SUCCESS',
  PARSE_PATIENT_XML_SKIPPED: 'PARSE_PATIENT_XML/SKIPPED',
  PARSE_PATIENT_XML_SUCCESS: 'PARSE_PATIENT_XML/SUCCESS',
  SAVE_PATIENT_DATA_SUCCESS: 'SAVE_PATIENT_DATA/SUCCESS',
  SAVE_PATIENT_DATA_FAILURE: 'SAVE_PATIENT_DATA/FAILURE',
  UPDATE_AI_RESULT_SUCCESS: 'UPDATE_AI_RESULT/SUCCESS',
  UPDATE_USER_FINDING_SUCCESS: 'UPDATE_USER_FINDING/SUCCESS',
  UPDATE_USER_FINDING_FAILURE: 'UPDATE_USER_FINDING/FAILURE',
  UPDATE_TOOTH_NUMBER: 'UPDATE_TOOTH_NUMBER',
  UPDATE_USER_NAME: 'UPDATE_USER_NAME',
  FETCH_PATIENTS_SUCCESS: 'FETCH_PATIENTS/SUCCESS',
  FETCH_PATIENTS_STARTED: 'FETCH_PATIENTS/STARTED',
  FETCH_PATIENTS_FAILURE: 'FETCH_PATIENTS/FAILURE',
  NEW_AI_STATS_FETCH_SUCCESS: 'NEW_AI_STATS_FETCH_SUCCESS',
  SWAP_IMAGE_INDICES: 'UPDATE_IMAGE_INDEX',
  RESET_IMAGES: 'RESET_IMAGES',
  VIEW_PATIENT: 'VIEW_PATIENT',
  WWWC: 'Wwwc',
  HANDLE_LOGIN_AS_DEMO_USER: 'HANDLE_LOGIN_AS_DEMO_USER',
  HANDLE_IMAGE_ROTATION: 'HANDLE_IMAGE_ROTATION',
  RESET_IMAGES_FOR_PATIENT: 'RESET_IMAGES_FOR_PATIENT',
  SAVE_USER_MODIFICATIONS_SUCCESS: 'SAVE_USER_MODIFICATIONS/SUCCESS',
  SAVE_USER_MODIFICATIONS_FAILURE: 'SAVE_USER_MODIFCATIONS/FAILURE',
  RECTANGLE_ROI: 'RectangleRoi',
  ELLIPTICAL_ROI: 'EllipticalRoi',
  MAGNIFY: 'Magnify',
  LENGTH: 'Length',
  IMAGE_ROTATIONS_LOCAL_STORAGE_KEY: 'IMAGE_ROTATIONS',
  INSIPIENT_MARKS,
  MARK_TO_BOX_PERCENTAGE_OF_IMAGE,
  DEFAULT_CONFIDENCE_THRESHOLDS: {
    LOW: 0.57,
    MEDIUM: 0.63,
    HIGH: 0.73,
    OPTIMAL: 0.63,
  },
  CONFIDENCE_PREFERENCES: {
    MORE_SELECTIVE: 2,
    BALANCED: 4,
    MORE_POTENTIAL: 6,
  },
  MODES: {
    VIEWING: 0,
    DRAWING: 1,
    MAGNIFYING: 2,
  },
  COLORS: colors,
  DASHLINES: {
    LOOSE: [5, 3],
    TIGHT: [3, 2],
  },
  VIEWS: {
    DOCTOR: 0,
    PATIENT: 1,
  },
  VALIDATIONS: {
    AGREE: 'Agree',
    MONITOR: 'Monitor',
    DISAGREE: 'Disagree',
  },
  AGREE_REASONS: [
    { value: 'filling', label: 'Filling' },
    { value: 'endodontic', label: 'Endodontic' },
    { value: 'crown', label: 'Crown' },
    { value: 'inlay/onlay', label: 'Inlay/onlay' },
    { value: 'preventative', label: 'Preventative' },
    { value: 'extraction', label: 'Extraction' },
  ],
  DISAGREE_REASONS: [
    { value: 'anatomical variation', label: 'Anatomical Variation' },
    { value: 'clinically disagree', label: 'Clinically Disagree' },
    { value: 'radiographic artifacts', label: 'Radiographic Artifacts' },
  ],
  DEMO_REASONS: [
    { value: 'D1110 Prophylaxis - adult', label: 'D1110 Prophylaxis - adult' },
    {
      value: 'D1206 Top Fl Varnish - adult',
      label: 'D1206 Top Fl Varnish - adult',
    },
    {
      value: 'D2392 2S resin filling, pos.',
      label: 'D2392 2S resin filling, pos.',
    },
    { value: 'D1351 Sealant - per tooth', label: 'D1351 Sealant - per tooth' },
    { value: 'D2950 Core buildup', label: 'D2950 Core buildup' },
    { value: 'D9504 Crown Delivery', label: 'D9504 Crown Delivery' },
    {
      value: 'D2391 1S resin filling, pos',
      label: 'D2391 1S resin filling, pos',
    },
    { value: 'D7140 Ext, erupted tooth', label: 'D7140 Ext, erupted tooth' },
    { value: 'D4910 Perio maintenance', label: 'D4910 Perio maintenance' },
    { value: 'D7210 Ext + bone removal', label: 'D7210 Ext + bone removal' },
    {
      value: 'D2393 3S resin filling, pos',
      label: 'D2393 3S resin filling, pos',
    },
    { value: 'D4341 PSRP, 4+', label: 'D4341 PSRP, 4+' },
    {
      value: 'D2331 2S resin filling, ant.',
      label: 'D2331 2S resin filling, ant.',
    },
    {
      value: 'D6740 Retainer crown ceramic',
      label: 'D6740 Retainer crown ceramic',
    },
    {
      value: 'D2330 1S resin filling, ant.',
      label: 'D2330 1S resin filling, ant.',
    },
    {
      value: 'D2332 3S resin filling, ant.',
      label: 'D2332 3S resin filling, ant.',
    },
    { value: 'D3310 Endo, ant tooth', label: 'D3310 Endo, ant tooth' },
    {
      value: 'D3320 Endo, premolar tooth',
      label: 'D3320 Endo, premolar tooth',
    },
    { value: 'D3330 Endo, molar tooth', label: 'D3330 Endo, molar tooth' },
    {
      value: 'D4381 Antimicro agnt delivery',
      label: 'D4381 Antimicro agnt delivery',
    },
    { value: 'D4342 PSRP, 1-3', label: 'D4342 PSRP, 1-3' },
    { value: 'D6240 Pontic PFM', label: 'D6240 Pontic PFM' },
    { value: 'D6750 Retainer crown PFM', label: 'D6750 Retainer crown PFM' },
    {
      value: 'D6010 Surg pl of implant body: endosteal implant',
      label: 'D6010 Surg pl of implant body: endosteal implant',
    },
  ],
  IMAGE_SEQUENCES: [
    // Top Row
    {
      primaryAnatomicStructureSequenceKey: 'T-54210',
      anatomicRegionSequenceName: 'Maxilla',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54220',
      anatomicRegionSequenceName: 'Maxilla',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54230',
      anatomicRegionSequenceName: 'Maxilla',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54240',
      anatomicRegionSequenceName: 'Maxilla',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54250',
      anatomicRegionSequenceName: 'Maxilla',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54260',
      anatomicRegionSequenceName: 'Maxilla',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54270',
      anatomicRegionSequenceName: 'Maxilla',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54280',
      anatomicRegionSequenceName: 'Maxilla',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54290',
      anatomicRegionSequenceName: 'Maxilla',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54300',
      anatomicRegionSequenceName: 'Maxilla',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54310',
      anatomicRegionSequenceName: 'Maxilla',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54320',
      anatomicRegionSequenceName: 'Maxilla',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54330',
      anatomicRegionSequenceName: 'Maxilla',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54340',
      anatomicRegionSequenceName: 'Maxilla',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54350',
      anatomicRegionSequenceName: 'Maxilla',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54360',
      anatomicRegionSequenceName: 'Maxilla',
    },

    // Bitewings, entries coming in pairs for what can go in this space
    // Left side
    {
      primaryAnatomicStructureSequenceKey: 'T-54210',
      anatomicRegionSequenceName: 'Jaw Region',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54220',
      anatomicRegionSequenceName: 'Jaw Region',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54230',
      anatomicRegionSequenceName: 'Jaw Region',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54240',
      anatomicRegionSequenceName: 'Jaw Region',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54250',
      anatomicRegionSequenceName: 'Jaw Region',
    },

    {
      primaryAnatomicStructureSequenceKey: 'T-54520',
      anatomicRegionSequenceName: 'Jaw Region',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54510',
      anatomicRegionSequenceName: 'Jaw Region',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54500',
      anatomicRegionSequenceName: 'Jaw Region',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54490',
      anatomicRegionSequenceName: 'Jaw Region',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54480',
      anatomicRegionSequenceName: 'Jaw Region',
    },

    // Right side of bitewings
    {
      primaryAnatomicStructureSequenceKey: 'T-54320',
      anatomicRegionSequenceName: 'Jaw Region',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54330',
      anatomicRegionSequenceName: 'Jaw Region',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54340',
      anatomicRegionSequenceName: 'Jaw Region',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54350',
      anatomicRegionSequenceName: 'Jaw Region',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54360',
      anatomicRegionSequenceName: 'Jaw Region',
    },

    {
      primaryAnatomicStructureSequenceKey: 'T-54410',
      anatomicRegionSequenceName: 'Jaw Region',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54400',
      anatomicRegionSequenceName: 'Jaw Region',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54390',
      anatomicRegionSequenceName: 'Jaw Region',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54380',
      anatomicRegionSequenceName: 'Jaw Region',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54370',
      anatomicRegionSequenceName: 'Jaw Region',
    },

    // Bottom row
    {
      primaryAnatomicStructureSequenceKey: 'T-54520',
      anatomicRegionSequenceName: 'Mandib',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54510',
      anatomicRegionSequenceName: 'Mandib',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54500',
      anatomicRegionSequenceName: 'Mandib',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54490',
      anatomicRegionSequenceName: 'Mandib',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54480',
      anatomicRegionSequenceName: 'Mandib',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54470',
      anatomicRegionSequenceName: 'Mandib',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54460',
      anatomicRegionSequenceName: 'Mandib',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54450',
      anatomicRegionSequenceName: 'Mandib',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54440',
      anatomicRegionSequenceName: 'Mandib',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54430',
      anatomicRegionSequenceName: 'Mandib',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54420',
      anatomicRegionSequenceName: 'Mandib',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54410',
      anatomicRegionSequenceName: 'Mandib',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54400',
      anatomicRegionSequenceName: 'Mandib',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54390',
      anatomicRegionSequenceName: 'Mandib',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54380',
      anatomicRegionSequenceName: 'Mandib',
    },
    {
      primaryAnatomicStructureSequenceKey: 'T-54370',
      anatomicRegionSequenceName: 'Mandib',
    },
  ],
  BITEWING: 'BITEWING',
  BITEWING_ABBREVIATION: 'BW',
  FMX: 'FMX',
  OTHER: 'Other',
  ODONTOGRAM: 'Odontogram',
  TOOTH_VIEW: {
    ALL: 'All',
    SELECTED: 'Selected',
  },
  TEST_IMAGE,
};

export const IMAGE_ROWS_FMX = 3;
export const IMAGE_ROWS_DEFAULT = 1;
export const IMAGE_ROW_COUNT_FMX = 7;
export const IMAGE_ROW_COUNT_DEFAULT = 4;
export const IMAGE_ROW_COUNT_STRIP = 18;

export const WINDOW_COLLAPSE_WIDTH = 976;
